import { useEffect, useState } from "react";
import { Brush, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { AuthImg } from "@/components/studio/AuthImg";
import { isFront } from "@/components/studio/ChapterArt";
import { confirmWithCost } from "@/lib/cost";
import { api, errMsg } from "@/lib/api";

const Tile = ({ label, children, testId }) => (
  <div className="flex flex-col gap-2" data-testid={testId}>
    <div className="aspect-square border border-white/15 flex items-center justify-center overflow-hidden">{children}</div>
    <div className="text-[10px] tracking-[0.22em] uppercase text-[#EDE3C8]/70">{label}</div>
  </div>
);

const Empty = ({ color }) => <span className="font-playfair italic text-sm" style={{ color }}>not drawn</span>;

export const ArtSetCard = ({ p, cat, theme, ensureSaved, onStarted, onRefresh }) => {
  const [style, setStyle] = useState(p.art_style || "");
  const [series, setSeries] = useState(false);
  const [busy, setBusy] = useState("");
  useEffect(() => setStyle(p.art_style || ""), [p.art_style]);

  const job = p.art_set_job || {};
  const running = job.status === "running";
  const chapters = (p.sections || []).filter((s) => !isFront(s.heading));
  const art = p.chapter_art || {};
  const drawn = chapters.filter((s) => art[s.id]);
  const missing = (p.illustration_path ? 0 : 1) + (p.back_art_path ? 0 : 1) + (chapters.length - drawn.length);
  const done = (job.finished || []).filter((f) => f !== "chapters").length + (p.chapter_art_job?.status && job.chapters ? p.chapter_art_job.done || 0 : 0);
  const v = `${theme.dark}${theme.accent}-${p.illustration_path}-${p.back_art_path}`;

  const call = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const suggest = () => call("suggest", async () => {
    const r = await api.post(`/products/${p.id}/art-style/suggest`);
    setStyle(r.data.art_style);
    toast("Style suggested — click Save style to keep it");
  });
  const saveStyle = () => call("save", async () => {
    await api.put(`/products/${p.id}/art-style`, { art_style: style, apply_to_category: series });
    toast.success(series ? `Style saved for every ${cat?.name} title without its own style` : "Style saved");
    onRefresh();
  });
  const draw = async (redraw) => {
    const n = redraw ? 2 + chapters.length : missing;
    if (!n) return toast("The set is complete — use “Redraw the whole set” to start again");
    if (!(await confirmWithCost(p.id, { kind: "art_set", redraw }, `${redraw ? "Redraw" : "Draw"} ${n} illustration${n === 1 ? "" : "s"} in one matching style?`))) return;
    call("draw", async () => {
      if (style !== (p.art_style || "")) await api.put(`/products/${p.id}/art-style`, { art_style: style, apply_to_category: series });
      await ensureSaved();
      const r = await api.post(`/products/${p.id}/art-set`, { redraw });
      toast.success(`Drawing ${r.data.total} matching illustrations — about a minute each, several at a time`);
      onStarted(r.data);
    });
  };

  return (
    <section className="p-6 md:p-8 text-[#EDE3C8]" style={{ background: theme.dark }} data-testid="art-set-card">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono-meta text-[11px] tracking-[0.24em] uppercase" style={{ color: theme.accent }}>Illustrated set</div>
          <h3 className="font-playfair text-3xl text-[#F3EBD6] mt-2">Cover, back vignette & chapters, one hand</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => draw(false)} disabled={running || !!busy} className="rounded-none gap-2" style={{ background: theme.accent, color: theme.dark }} data-testid="art-set-draw-btn">
            {running ? <Loader2 size={15} className="animate-spin" /> : <Brush size={15} />}
            {running ? `Drawing ${done} of ${job.total}` : missing ? `Draw the full set (${missing})` : "Set complete"}
          </Button>
          <Button variant="outline" onClick={() => draw(true)} disabled={running || !!busy} className="rounded-none bg-transparent text-[#EDE3C8] border-white/25 hover:bg-white/10 hover:text-white" data-testid="art-set-redraw-btn">Redraw the whole set</Button>
        </div>
      </div>
      <div className="grid lg:grid-cols-12 gap-8 mt-6">
        <div className="lg:col-span-5 space-y-3">
          <div className="text-xs text-[#EDE3C8]/80">Series style: every drawing in this book follows it. Leave empty and the AI designs one when you draw.</div>
          <Textarea rows={5} value={style} onChange={(e) => setStyle(e.target.value)} placeholder={cat?.art_style || "e.g. Fine copperplate engraving after 15th-century Florentine woodcuts; dense cross-hatching; each subject framed by a slim arched niche; generous empty space."} className="bg-white/5 border-white/20 text-[#F3EBD6] placeholder:text-[#EDE3C8]/40" data-testid="art-style-input" />
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <Checkbox checked={series} onCheckedChange={(c) => setSeries(!!c)} className="border-white/40" data-testid="art-style-series-checkbox" />
            Use as the series style for every {cat?.name || "category"} title
          </label>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={suggest} disabled={!!busy || running} className="rounded-none gap-1.5 bg-transparent text-[#EDE3C8] border-white/25 hover:bg-white/10 hover:text-white" data-testid="art-style-suggest-btn"><Sparkles size={13} />{busy === "suggest" ? "Designing…" : "Suggest with AI"}</Button>
            <Button size="sm" onClick={saveStyle} disabled={!!busy} className="rounded-none" style={{ background: theme.accent, color: theme.dark }} data-testid="art-style-save-btn">{busy === "save" ? "Saving…" : "Save style"}</Button>
          </div>
          {!running && job.errors?.length > 0 && <p className="text-xs text-red-200" data-testid="art-set-errors">{job.errors.length} couldn't be drawn: {job.errors[0]}</p>}
        </div>
        <div className="lg:col-span-7 grid grid-cols-3 gap-4">
          <Tile label="Cover" testId="art-set-cover">
            {p.illustration_path ? <AuthImg path={`/products/${p.id}/illustration.png`} version={v} alt="" className="w-full h-full object-cover" testId="art-set-cover-img" /> : <Empty color={theme.accent} />}
          </Tile>
          <Tile label="Back vignette" testId="art-set-back">
            {p.back_art_path ? <AuthImg path={`/products/${p.id}/back-art.png`} version={v} alt="" className="w-full h-full object-cover" testId="art-set-back-img" /> : <Empty color={theme.accent} />}
          </Tile>
          <Tile label={`Chapters · ${drawn.length} of ${chapters.length}`} testId="art-set-chapters">
            {drawn.length ? (
              <div className={`grid ${drawn.length > 1 ? "grid-cols-2" : "grid-cols-1"} gap-px w-full h-full`}>
                {drawn.slice(0, 4).map((s) => <AuthImg key={s.id} path={`/products/${p.id}/chapter-art/${s.id}.png`} version={`${art[s.id]}${theme.dark}`} alt="" className="w-full h-full object-cover" />)}
              </div>
            ) : <Empty color={theme.accent} />}
          </Tile>
        </div>
      </div>
      <p className="text-[11px] text-[#EDE3C8]/60 mt-5">When it finishes, click “Rebuild files” to put the set into the PDF, toolkit and Instagram images.</p>
    </section>
  );
};
